/**
 * The days:hours:minutes:seconds countdown the phase surfaces share — the
 * Reveal phases cards, the product promo strip, the sticky bar and the cart
 * drawer promo. `snippets/phase-countdown.liquid` prints the digits already on
 * the right second, so this only keeps them moving.
 *
 * The deadline arrives as absolute milliseconds, worked out from the Bulgarian
 * wall clock the merchant typed. Nothing here re-reads a date string, so the
 * visitor's own timezone never gets a say in when the phase ends.
 *
 * Once the deadline passes the element hides itself. The "or" that hands over
 * to the progress bar lives inside it, so it goes at the same moment and the
 * bar takes the row back without the page having to reload.
 *
 * The cart drawer replaces its own markup on every cart update, so the element
 * has to survive being disconnected and connected again: `connectedCallback`
 * re-reads the DOM each time rather than assuming it only ever runs once.
 */
if (!customElements.get('phase-countdown')) {
  customElements.define(
    'phase-countdown',
    class PhaseCountdown extends HTMLElement {
      connectedCallback() {
        this.end = Number(this.dataset.deadline);
        this.row = this.querySelector('[role="timer"]');
        this.cells = ['days', 'hours', 'minutes', 'seconds'].map((unit) => this.querySelector(`[data-unit="${unit}"]`));

        if (!Number.isFinite(this.end) || this.end <= 0 || !this.row || !this.cells.every(Boolean)) {
          this.cells = null;
          return;
        }

        this.tick();
        this.interval ??= setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.interval);
        this.interval = null;
        this.cells = null;
      }

      tick() {
        const remaining = Math.max(0, Math.floor((this.end - Date.now()) / 1000));

        this.hidden = remaining === 0;

        if (remaining === 0) {
          clearInterval(this.interval);
          this.interval = null;
          return;
        }

        const values = [
          Math.floor(remaining / 86400),
          Math.floor(remaining / 3600) % 24,
          Math.floor(remaining / 60) % 60,
          remaining % 60,
        ].map((value) => String(value).padStart(2, '0'));

        values.forEach((value, index) => {
          if (this.cells[index].textContent !== value) this.cells[index].textContent = value;
        });

        if (this.dataset.ariaTemplate) {
          this.row.setAttribute(
            'aria-label',
            this.dataset.ariaTemplate
              .replace('{days}', values[0])
              .replace('{hours}', values[1])
              .replace('{minutes}', values[2])
              .replace('{seconds}', values[3])
          );
        }
      }
    }
  );
}
